import React, { FC, useCallback, useMemo } from 'react';
import Badge from '@atlaskit/badge';
import Lozenge from '@atlaskit/lozenge';
import WorldIcon from '@atlaskit/icon/glyph/world';
import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';
import { token } from '@atlaskit/tokens';
import { SloGroupCohortSummaryDTO } from '@atlassiansox/phobos-client-ts';
import Pagination from '@atlaskit/pagination';
import { useQuery } from '@tanstack/react-query';

import { flexStart, spaceGapCompact, spaceTopCozy, colors, marginPushRight } from '@/styles';
import { KeyValue } from '@/components/primitive';
import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';
import { useFetchErrorController } from '@/controllers';
import { Params } from '@/components/composed/catalog-search-bar/types';

import { badgeStyles, generalBarStyles } from '../../styles';
import { getPagesFromTotalPage } from '../../../search/utils';
import { BASE_SLOS_PAGE_SIZE } from '../../constants';
import { mapParamsToAPIQuery } from '../../../catalog-search-bar/services/utils';
import SearchPageSkeleton from '../../../search/search-results/search-page-skeleton';
import { CohortMemberCohortTypeEnum } from '../../types';
import { cohortTitleStyles, iconSizeAlignFixStyles, iconSizeAlignStyles } from '../styles';
import SLOCohortMembers from '../slo-cohort-members';

import SLOCohortMimirFilter from './slo-cohort-mimir-filter';

type SLOCohortMimirProps = {
  sloGroupCohortSummary: SloGroupCohortSummaryDTO;
  publicId: string;
  hasBreachStatusOnly?: string;
  cohorts: Map<string, string>;
};

const SLOCohortMimir: FC<SLOCohortMimirProps> = ({
  sloGroupCohortSummary,
  publicId,
  hasBreachStatusOnly,
  cohorts,
}) => {
  const { cohort, draft } = sloGroupCohortSummary;
  const cohortIcon = useMemo(() => {
    switch (cohort) {
      case CohortMemberCohortTypeEnum.Global:
        return (
          <span css={[iconSizeAlignStyles, iconSizeAlignFixStyles]}>
            <WorldIcon
              primaryColor={token('color.text.subtlest', colors.N100)}
              label=""
              size="small"
            />
          </span>
        );
      default:
        return <BulletListIcon primaryColor={token('color.text.subtlest', colors.N100)} label="" />;
    }
  }, [cohort]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const { onError } = useFetchErrorController();

  const handleChangePage = useCallback((_: any, page: number) => {
    setCurrentPage(page);
  }, []);

  const { queryKey, path } = useMemo(() => {
    const param: string = `&levels=${cohort}&${mapParamsToAPIQuery(
      {} as Params,
      false
    )}&size=${BASE_SLOS_PAGE_SIZE}${hasBreachStatusOnly ? '&hasBreachStatusOnly=true' : ''}&${
      cohorts.get(cohort) ? `cohortMembers=${cohorts.get(cohort)}` : ''
    }`;

    return {
      ...querySpec('slo-group.{id}.mimir.slos.{params}.{page}', {
        id: publicId,
        param,
        page: currentPage.toString(),
      }),
    };
  }, [publicId, hasBreachStatusOnly, currentPage, cohort, cohorts]);

  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const pages: number[] = useMemo(() => getPagesFromTotalPage(data?.totalPages), [data]);
  const showPagination: boolean = (data?.totalPages ?? 0) > 1;

  if (isLoading) return <SearchPageSkeleton isCompact />;
  if (!data.content?.length) return null;

  return (
    <div>
      <div css={[flexStart, spaceGapCompact, generalBarStyles, cohortTitleStyles]}>
        {cohortIcon}
        <KeyValue noPositionFix title="cohort">
          <Badge>
            <span css={[badgeStyles]}>{cohort}</span>
          </Badge>
        </KeyValue>
        {draft && <Lozenge appearance="moved">draft</Lozenge>}
        <SLOCohortMimirFilter publicId={publicId} cohort={cohort} />
        {showPagination && (
          <div css={marginPushRight}>
            <Pagination selectedIndex={currentPage - 1} pages={pages} onChange={handleChangePage} />
          </div>
        )}
      </div>
      <div css={spaceTopCozy}>
        <SLOCohortMembers ignoreRollup slos={data.content} />
      </div>
    </div>
  );
};

export default SLOCohortMimir;
